import React, { useState, useEffect, useCallback, useRef } from 'react';
import { tradesAPI, profileAPI, hatsAPI, usersAPI, settingsAPI, balanceAPI, shippingAPI } from '../services/api';
import { safeNumber, safeMathOperation, formatCurrency, calculateTieredFee } from '../utils/calculations';

const TradeModal = ({ isOpen, onClose, selectedHat, isLoggedIn }) => {
  // Use live Eastern Time for date calculations
  const getNowET = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const computeFirstAvailableET = useCallback(() => {
    const d = getNowET();
    d.setDate(d.getDate() + 3);
    return d;
  }, []);
  const ymd = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  
  // Refs for scrolling to buttons
  const checkEligibilityRef = useRef(null);
  const calculateShippingRef = useRef(null);
  
  // Function to scroll to the appropriate button based on delivery method
  const scrollToDeliveryButton = (deliveryMethod) => {
    setTimeout(() => {
      if (deliveryMethod === 'pickup' && checkEligibilityRef.current) {
        checkEligibilityRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      } else if (deliveryMethod === 'shipping' && calculateShippingRef.current) {
        calculateShippingRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100); // Small delay to ensure DOM is updated
  };
  
  const [formData, setFormData] = useState({
    selectedHat: '',
    duration: '7',
    startDate: '',
    endDate: '',
    deliveryMethod: '',
    userZipcode: '',
    fromZipcode: '',
    toZipcode: '',
    message: ''
  });
  const [myHats, setMyHats] = useState([]);

  const [distanceResult, setDistanceResult] = useState('');
  const [shippingResult, setShippingResult] = useState('');
  const [shippingCost, setShippingCost] = useState(null);
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [feeInfo, setFeeInfo] = useState({ fee: 0 });
  const [balanceInfo, setBalanceInfo] = useState({ balance: 0 });
  const [previewInfo, setPreviewInfo] = useState({ hold: 0, fee: 0, totalCharge: 0, balanceAfter: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [promoActive, setPromoActive] = useState(false);
  const [userEligible, setUserEligible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // First available date is 3 days from current Eastern time
      const today = ymd(computeFirstAvailableET());
      setFormData(prev => ({
        ...prev,
        startDate: today,
        endDate: calculateEndDate(today, parseInt(prev.duration))
      }));
      setError('');
      setSuccess('');
      setShippingCalculated(false);

      // Load fee and current balance
      (async () => {
        try {
          const settings = await settingsAPI.get();
          const bal = await balanceAPI.getBalance();
          const profile = await profileAPI.get();
          
          // DEBUG: Log promotion-related data
          console.log('[PROMO DEBUG] Settings from API:', settings);
          console.log('[PROMO DEBUG] first_trade_free_promo_active:', settings?.first_trade_free_promo_active);
          console.log('[PROMO DEBUG] Profile data:', profile);
          console.log('[PROMO DEBUG] hasUsedFreeTrade:', profile?.hasUsedFreeTrade);
          
          // Check if user is eligible for first trade free
          const promoIsActive = settings?.first_trade_free_promo_active || false;
          const hasNotUsedFreeTrade = !profile?.hasUsedFreeTrade;
          const isEligible = promoIsActive && hasNotUsedFreeTrade;
          
          console.log('[PROMO DEBUG] promoIsActive:', promoIsActive);
          console.log('[PROMO DEBUG] hasNotUsedFreeTrade:', hasNotUsedFreeTrade);
          console.log('[PROMO DEBUG] isEligible:', isEligible);
          
          setUserEligible(isEligible);
          setPromoActive(promoIsActive);
          
          // Calculate fees - if eligible, show $0, otherwise show calculated fee
          const calculatedFee = calculateTieredFee(selectedHat?.value || 0);
          const siteFee = isEligible ? 0 : calculatedFee;
          const pickupFee = Number(settings?.pickup_fee || 0);
          
          setFeeInfo({ fee: siteFee, pickup: pickupFee });
          
          // Use available_balance instead of total balance for accurate calculations
          setBalanceInfo({ 
            balance: Number(bal?.available_balance || bal?.balance || 0),
            totalBalance: Number(bal?.balance || 0),
            activeFees: Number(bal?.active_fees || 0)
          });
        } catch (_) {}
      })();

      // Prefill shipping/pickup fields from user profile and owner's ZIP
      (async () => {
        try {
          const profile = await profileAPI.get();
          // Attempt to fetch owner's ZIP via public closet endpoint if available
          let ownerZip = selectedHat?.owner_zip || '';
          try {
            const ownerId = selectedHat?.user_id || selectedHat?.owner_id;
            if (ownerId) {
              const closet = await usersAPI.getCloset(ownerId, { page: 1, perPage: 1 });
              ownerZip = closet?.profile?.zip_code || ownerZip;
            }
          } catch (_) { /* non-fatal */ }
          setFormData(prev => ({
            ...prev,
            fromZipcode: profile?.zip_code || prev.fromZipcode,
            userZipcode: profile?.zip_code || prev.userZipcode,
            toZipcode: ownerZip || prev.toZipcode
          }));
        } catch (e) {
          // Silent fail, keep defaults
        }
      })();

      // Load only the current user's hats for selection
      (async () => {
        try {
          const hats = await hatsAPI.getMyHats();
          setMyHats(Array.isArray(hats) ? hats : []);
        } catch (e) {
          setMyHats([]);
        }
      })();
    }
  }, [isOpen, computeFirstAvailableET, selectedHat, selectedHat?.owner_id, selectedHat?.owner_zip, selectedHat?.user_id, selectedHat?.value]);

  const calculateEndDate = (startDate, duration) => {
    if (duration === 'custom') {
      // For custom, don't auto-calculate - let user choose dates manually
      return '';
    }
    const endDate = new Date(startDate);
    const daysToAdd = parseInt(duration) || 0;
    endDate.setDate(endDate.getDate() + daysToAdd);
    return endDate.toISOString().split('T')[0];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      if (name === 'startDate' || name === 'duration') {
        // Only auto-calculate end date if not using custom duration
        if (name === 'duration' && value === 'custom') {
          // Clear end date when switching to custom
          newData.endDate = '';
        } else if (name === 'duration' && value !== 'custom') {
          // Auto-calculate when switching from custom to preset duration
          newData.endDate = calculateEndDate(prev.startDate, parseInt(value));
        } else if (name === 'startDate' && prev.duration !== 'custom') {
          // Auto-calculate when changing start date (only if not custom)
          newData.endDate = calculateEndDate(value, parseInt(prev.duration));
        }
      }
      
      return newData;
    });
  };

  // Compute preview of charges when delivery method or selection changes
  useEffect(() => {
    const compute = async () => {
      try {
        // Need selected hat values to figure out hold amount: hold is value of the other user's item
        let holdValue = 0;
        if (selectedHat && selectedHat.value) {
          holdValue = Number(selectedHat.value) || 0;
        }
        const fee = Number(feeInfo.fee || 0);
        // Only include delivery fees if they've been calculated
        const isShip = formData.deliveryMethod === 'shipping';
        const isPickup = formData.deliveryMethod === 'pickup';
        const roundTripShippingCost = Number(shippingCost || 0); // Backend returns round-trip cost
        
        // Only include delivery costs if they've been calculated
        let deliveryRoundTrip = 0;
        if (isShip && shippingCost !== null) {
          deliveryRoundTrip = roundTripShippingCost; // Backend returns round-trip cost directly
        } else if (isPickup && distanceResult && distanceResult.includes('✅')) {
          deliveryRoundTrip = Number(feeInfo.pickup || 0);
        }
        
        // Apply first trade free promo to shipping fees if user is eligible
        const finalDeliveryFees = userEligible ? 0 : deliveryRoundTrip;
        
        // Only hold is due now at submit; fees are charged on accept
        const balance = Number(balanceInfo.balance || 0); // This is now available_balance
        const totalFeesOnAccept = fee + finalDeliveryFees;
        const totalChargeNow = holdValue; // Only hold amount
        const totalDebitsExcludingHold = totalFeesOnAccept; // New concept: debits excluding hold
        const balanceAfter = balance - totalDebitsExcludingHold; // New calculation
        setPreviewInfo({ 
          hold: holdValue, 
          fee, 
          totalFeesOnAccept, 
          totalCharge: totalChargeNow, 
          balanceAfter, 
          totalDebitsExcludingHold, // New field
          sendCost: finalDeliveryFees / 2, // Half of round-trip for send
          returnCost: finalDeliveryFees / 2, // Half of round-trip for return
          deliveryRoundTrip: finalDeliveryFees 
        });
      } catch (_) {
        setPreviewInfo({ 
          hold: 0, 
          fee: Number(feeInfo.fee || 0), 
          totalFeesOnAccept: 0, 
          totalCharge: 0, 
          balanceAfter: Number(balanceInfo.balance || 0), 
          totalDebitsExcludingHold: 0,
          sendCost: 0, 
          returnCost: 0, 
          deliveryRoundTrip: 0 
        });
      }
    };
    compute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [selectedHat?._id, selectedHat?.value, feeInfo.fee, balanceInfo.balance, balanceInfo.totalBalance, balanceInfo.activeFees, formData.deliveryMethod, shippingCost, distanceResult, userEligible]);

  // Backend-powered helpers
  const checkPickupEligible = async () => {
    try {
      const res = await tradesAPI.pickupEligible(selectedHat?._id);
      return res;
    } catch (e) {
      throw e;
    }
  };

  // const getShippingEstimate = async () => {
  //   try {
  //     const res = await tradesAPI.shippingEstimate(selectedHat?._id);
  //     return res;
  //   } catch (e) {
  //     throw e;
  //   }
  // };

  const handleCheckDistance = async () => {
    try {
      setDistanceResult('');
      const result = await checkPickupEligible();
      if (result.eligible) {
        const pickupFeeDisplay = `$${Number(feeInfo?.pickup || 5).toFixed(2)} fee`;
            setDistanceResult(`✅ You both qualify for pickup (${pickupFeeDisplay}). Distances to 33068 — You: ~${result.distances?.initiator_to_33068} mi, Owner: ~${result.distances?.owner_to_33068} mi`);
      } else {
        const yours = result.distances?.initiator_to_33068;
        const theirs = result.distances?.owner_to_33068;
        const reason = result.reason ? ` Reason: ${result.reason}` : '';
        setDistanceResult(`❌ Not eligible for pickup. You: ${yours ?? 'N/A'} mi, Owner: ${theirs ?? 'N/A'} mi from 33068.${reason}`);
      }
    } catch (e) {
      setDistanceResult(e.message || 'Failed to check pickup eligibility.');
    }
  };

  const handleCalculateShipping = async () => {
    try {
      setIsCalculatingShipping(true);
      setShippingResult('');
      
      // Validate zip codes
      if (!formData.fromZipcode || !formData.toZipcode) {
        setShippingResult('Please enter both from and to zip codes.');
        return;
      }
      
      if (formData.fromZipcode.length !== 5 || formData.toZipcode.length !== 5) {
        setShippingResult('Please enter valid 5-digit zip codes.');
        return;
      }
      
      // Get real Shippo rates
      const ratesResponse = await shippingAPI.calculateRates(formData.fromZipcode, formData.toZipcode);
      
      if (ratesResponse.rates && ratesResponse.rates.length > 0) {
        const cheapestRate = ratesResponse.cheapest_rate || ratesResponse.rates[0];
        setShippingCost(Number(cheapestRate.amount || 0));
        setShippingCalculated(true);
        
        // Display only the cheapest rate
        let resultText = `📦 Estimated shipping cost: $${cheapestRate.amount}\n⏱️ Estimated delivery: ${cheapestRate.estimated_days} business days\n🚚 Service: ${cheapestRate.service}`;
        
        setShippingResult(resultText);
      } else {
        setShippingResult('No shipping rates available for this route.');
      }
    } catch (e) {
      console.error('Shipping calculation error:', e);
      setShippingResult(e.message || 'Failed to calculate shipping rates.');
    } finally {
      setIsCalculatingShipping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!isLoggedIn) {
      setError('Please log in to request a trade');
      return;
    }
    
    if (!formData.selectedHat) {
      setError('Please select an item to trade');
      return;
    }

    // Prevent trading own items
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (currentUser && selectedHat && String(selectedHat.user_id) === String(currentUser.id)) {
        setError("You can't trade your own item.");
        return;
      }
    } catch (_) {}
    
    if (!formData.deliveryMethod) {
      setError('Please select a delivery method');
      return;
    }
    
    // Validate delivery requirements
    if (formData.deliveryMethod === 'pickup') {
      // Check if pickup eligibility has been verified
      if (!distanceResult || !distanceResult.includes('✅')) {
        setError('Please check pickup eligibility first to see if you qualify and view the pickup cost.');
        // Automatically check pickup eligibility and scroll to top
        try {
          await handleCheckDistance();
          // Scroll to top of modal to show the pickup eligibility result
          const modalContent = document.querySelector('.modal-content');
          if (modalContent) {
            modalContent.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (e) {
          setError('Failed to check pickup eligibility. Please try again.');
        }
        return;
      }
      
      // If we get here, eligibility was already checked and passed
      try {
        const result = await checkPickupEligible();
        if (!result.eligible) {
              setError('Pickup not eligible: both users must be within 75 miles of 33068.');
          return;
        }
      } catch (e) {
        setError(e.message || 'Failed to verify pickup eligibility.');
        return;
      }
    } else if (formData.deliveryMethod === 'shipping') {
      // Check if shipping cost has been calculated
      if (!shippingCalculated) {
        setError('Please calculate shipping costs first to see the total cost before submitting.');
        // Automatically calculate shipping cost and scroll to top
        try {
          await handleCalculateShipping();
          // Scroll to top of modal to show the shipping cost
          const modalContent = document.querySelector('.modal-content');
          if (modalContent) {
            modalContent.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (e) {
          setError('Failed to calculate shipping costs. Please try again.');
        }
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      // For demo purposes, we'll use placeholder item IDs
      // In a real app, you'd get the actual item IDs from the user's inventory
      const tradeData = {
        hat1_id: formData.selectedHat, // User's hat id
        hat2_id: selectedHat._id, // Selected hat from marketplace
        start_date: formData.startDate,
        end_date: formData.endDate,
        delivery_method: formData.deliveryMethod,
        message: (formData.message || '').trim(),
        shipping_cost: formData.deliveryMethod === 'shipping' ? shippingCost : null, // Send calculated shipping cost
        duration: formData.duration === 'custom' ? 0 : parseInt(formData.duration) || 0
      };
      
      await tradesAPI.create(tradeData);
      
      setSuccess('Trade request submitted successfully! The other user will be notified.');
      
      // Reset form
      setFormData({
        selectedHat: '',
        duration: '7',
        startDate: '',
        endDate: '',
        deliveryMethod: '',
        userZipcode: '',
        fromZipcode: '',
        toZipcode: '',
        message: ''
      });
      setDistanceResult('');
      setShippingResult('');
      setShippingCalculated(false);
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Trade request error:', error);
      setError(error.message || 'Failed to submit trade request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <span className="close" onClick={onClose}>&times;</span>
        <h2>Request Trade</h2>
        
        {error && (
          <div style={{ 
            color: '#ff4444', 
            backgroundColor: '#ffe6e6', 
            padding: '10px', 
            borderRadius: '5px', 
            marginBottom: '15px' 
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{ 
            color: '#28a745', 
            backgroundColor: '#d4edda', 
            padding: '10px', 
            borderRadius: '5px', 
            marginBottom: '15px' 
          }}>
            {success}
          </div>
        )}
        
        <div className="trade-details">
          <div className="hat-info">
            <img 
              src={selectedHat?.image_url || selectedHat?.image || ''} 
              alt="Selected Item" 
              id="modal-hat-image"
            />
            <h3 id="modal-hat-name">{selectedHat?.name || 'Item Name'}</h3>
            <p id="modal-hat-description">{selectedHat?.description || 'Description'}</p>
          </div>
          
          <form className="trade-form" onSubmit={handleSubmit}>
            {
              <div className="form-group" style={{ background:'#f9fafb', padding:12, borderRadius:6, border:'1px solid #eee', marginBottom:16 }}>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {promoActive && (
                    <div style={{ background:'#e8f5e9', padding:8, borderRadius:4, marginBottom:8, color:'#2e7d32', fontSize:'0.9rem', fontWeight:500 }}>
                      🎉 First Trade Free Promotion Active! If this is your first trade, site fee and shipping fees will be waived.
                    </div>
                  )}
                  <div><strong>Site Fee (charged on accept):</strong> {formatCurrency(safeNumber(feeInfo.fee))}</div>
                  {formData.deliveryMethod === 'pickup' && (
                    <div><strong>Pickup Convenience Fee (on accept):</strong> {userEligible ? formatCurrency(0) : formatCurrency(safeNumber(feeInfo.pickup))}</div>
                  )}
                  {formData.deliveryMethod === 'shipping' && (
                    <div>
                      <strong>Estimated Shipping Fees (on accept):</strong>{' '}
                      {userEligible ? formatCurrency(0) : (shippingCost != null ? `${formatCurrency(safeNumber(shippingCost))} (${formatCurrency(safeMathOperation(() => safeNumber(shippingCost) / 2, 0))} each way)` : '—')}
                    </div>
                  )}
                  <div><strong>Refundable Hold (other item's value):</strong> {formatCurrency(safeNumber(previewInfo.hold))}</div>
                  <div><strong>Total debits:</strong> {formatCurrency(safeNumber(previewInfo.totalDebitsExcludingHold))}</div>
                  <div><strong>Your balance:</strong> {formatCurrency(safeNumber(balanceInfo.balance))} → {formatCurrency(safeNumber(previewInfo.balanceAfter))}</div>
                  <small style={{ color:'#666' }}>Only the hold amount is reserved now. Shipping and site fees are charged when the trade is accepted.</small>
                </div>
              </div>
            }
            <div className="form-group">
              <label>Select Your Item to Trade</label>
              <select 
                name="selectedHat"
                value={formData.selectedHat}
                onChange={handleInputChange}
              >
                <option value="">Choose your item...</option>
                {myHats.map((hat) => (
                  <option key={hat._id} value={hat._id}>
                    {hat.name} {hat.value ? `(Value: ${formatCurrency(safeNumber(hat.value))})` : ''}
                  </option>
                ))}
              </select>
              {myHats.length === 0 && (
                <div style={{ marginTop: '8px', color: '#666' }}>
                  You have no items to trade yet. Add one in your Dashboard → Upload Item.
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Trade Duration</label>
              <div className="duration-selector">
                <select 
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                >
                  <option value="3">3 Days</option>
                  <option value="7">1 Week</option>
                  <option value="14">2 Weeks</option>
                  <option value="30">1 Month</option>
                  <option value="custom">Custom (choose dates)</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label>Start Date</label>
              <input 
                type="date" 
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                min={ymd(computeFirstAvailableET())}
                readOnly={false}
              />
            </div>
            
            <div className="form-group">
              <label>End Date</label>
              <input 
                type="date" 
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                min={formData.startDate ? (() => {
                  const startDate = new Date(formData.startDate);
                  const minDate = new Date(startDate);
                  minDate.setDate(minDate.getDate() + 3);
                  return minDate.toISOString().split('T')[0];
                })() : ''}
                max={formData.startDate ? (() => {
                  const startDate = new Date(formData.startDate);
                  const maxDate = new Date(startDate);
                  maxDate.setDate(maxDate.getDate() + 30);
                  return maxDate.toISOString().split('T')[0];
                })() : ''}
                readOnly={formData.duration !== 'custom'}
              />
            </div>
            
            <div className="form-group">
              <label>Delivery Method</label>
              <div className="delivery-chips">
                <div
                  className={`option-chip ${formData.deliveryMethod === 'pickup' ? 'selected' : ''}`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, deliveryMethod: 'pickup' }));
                    scrollToDeliveryButton('pickup');
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { 
                    if (e.key === 'Enter' || e.key === ' ') {
                      setFormData(prev => ({ ...prev, deliveryMethod: 'pickup' }));
                      scrollToDeliveryButton('pickup');
                    }
                  }}
                >
                  Pickup
                </div>
                <div
                  className={`option-chip ${formData.deliveryMethod === 'shipping' ? 'selected' : ''}`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, deliveryMethod: 'shipping' }));
                    scrollToDeliveryButton('shipping');
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { 
                    if (e.key === 'Enter' || e.key === ' ') {
                      setFormData(prev => ({ ...prev, deliveryMethod: 'shipping' }));
                      scrollToDeliveryButton('shipping');
                    }
                  }}
                >
                  Shipping
                </div>
              </div>
            </div>
            
            {formData.deliveryMethod === 'pickup' && (
              <div className="pickup-section">
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#856404'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>🚗 Pickup Information</div>
                  <div>Pickups are scheduled for the closest Thursday or Sunday in term dates. Please ensure you're available during the scheduled pickup window.</div>
                </div>
                <div className="form-group">
                  <label>Your ZIP (from your profile)</label>
                  <div className="zip-input-group">
                    <input 
                      type="text" 
                      name="userZipcode"
                      value={formData.userZipcode}
                      placeholder="Saved on your profile"
                      maxLength="5"
                      readOnly
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Owner&apos;s ZIP (from their profile)</label>
                  <div className="zip-input-group">
                    <input 
                      type="text" 
                      name="toZipcode"
                      value={formData.toZipcode}
                      placeholder="Saved on owner's profile"
                      maxLength="5"
                      readOnly
                    />
                  </div>
                </div>
                <div className="form-group">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleCheckDistance}
                    ref={checkEligibilityRef}
                  >
                    Check Eligibility
                  </button>
                  {distanceResult && (
                    <div className="distance-result">
                      {distanceResult}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {formData.deliveryMethod === 'shipping' && (
              <div className="shipping-section">
                <div style={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#1976d2'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>📦 Shipping Information</div>
                  <div>Items must fit in our standard shipping box (15" × 8" × 14"). Please ensure your item will fit these dimensions.</div>
                </div>
                <div className="form-group">
                  <label>Your ZIP (from your profile)</label>
                  <input 
                    type="text" 
                    name="fromZipcode"
                    value={formData.fromZipcode}
                    placeholder="Saved on your profile"
                    maxLength="5"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Owner&apos;s ZIP (from their profile)</label>
                  <input 
                    type="text" 
                    name="toZipcode"
                    value={formData.toZipcode}
                    placeholder="Saved on owner's profile"
                    maxLength="5"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleCalculateShipping}
                    ref={calculateShippingRef}
                    disabled={isCalculatingShipping}
                  >
                    {isCalculatingShipping ? 'Calculating...' : 'Calculate Shipping Cost'}
                  </button>
                  {shippingResult && (
                    <div className="shipping-result">
                      {shippingResult}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label>Message (Optional)</label>
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Add a personal message to the trade request..."
                rows="3"
              />
            </div>
            
            <div style={{fontSize: '12px', color: '#666', marginBottom: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef'}}>
              <strong>Important:</strong> By submitting this trade request, you agree that non-returned or damaged items may result in monetary penalties and forfeiture of your item value hold to repair or replace damaged items.
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Submit Trade Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TradeModal; 